import { useEffect, useState } from "react"
import { Skeleton } from "@/primitives/skeleton.tsx"
import { cn } from "@/primitives/utils"

/*
 * What a page looks like while its data loads: grey blocks at the real geometry of what will
 * replace them, under the PageHeader, which renders at once with its real crumbs. Nothing says
 * "One moment…".
 *
 * It waits 150ms before showing, so a load that is quick anyway never flashes.
 */

/** How long a load runs before its skeleton shows. */
const showAfter = 150

/**
 * Whether a skeleton that mounted `ms` ago should show yet: false until then, so a load that is
 * quick anyway never flashes grey. A skeleton that holds its place while hidden (a section's, a
 * card grid's) stays at its real size and only turns visible, so nothing moves when it does.
 */
export function useShownAfter(ms: number = showAfter) {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setShown(true), ms)
    return () => clearTimeout(timer)
  }, [ms])
  return shown
}

/** Row title widths, fixed so the skeleton reads as varied text yet never moves between renders. */
const titleWidths = [
  "w-31/50",
  "w-12/25",
  "w-7/10",
  "w-11/20",
  "w-2/5",
  "w-33/50",
  "w-13/25",
  "w-11/25",
] as const

/** Cards per column on a Board skeleton, left to right. */
const boardCards = [3, 2, 3, 1] as const

function ListRows() {
  return titleWidths.map((width) => (
    <div key={width} className="flex h-9 items-center gap-3 border-b border-border-subtle px-6">
      <Skeleton className="size-4 rounded-full" />
      <Skeleton className="h-3 w-12" />
      <Skeleton className={cn("h-3", width)} />
    </div>
  ))
}

function Board() {
  return (
    <div className="flex gap-2 px-6 py-4">
      {boardCards.map((cards, column) => (
        <div
          key={column}
          className="flex w-64 shrink-0 flex-col gap-1.5 rounded-lg bg-surface-sunken p-1.5"
        >
          <div className="flex h-8 items-center gap-2 px-1.5">
            <Skeleton className="size-3.5 rounded-full" />
            <Skeleton className="h-3 w-20" />
          </div>
          {Array.from({ length: cards }, (_, card) => (
            <Skeleton key={card} className="h-16 bg-card ring-1 ring-border" />
          ))}
        </div>
      ))}
    </div>
  )
}

function Document() {
  return (
    <div className="flex min-h-full">
      <div className="min-w-0 flex-1">
        <div className="mx-auto max-w-3xl space-y-4 px-10 pt-8">
          <Skeleton className="h-8 w-2/3" />
          <div className="space-y-2.5 pt-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-11/12" />
            <Skeleton className="h-3.5 w-3/5" />
          </div>
        </div>
      </div>
      <div className="w-72 shrink-0 space-y-0.5 border-l border-border-subtle px-4 py-6">
        {titleWidths.slice(0, 6).map((width) => (
          <div key={width} className="flex h-8 items-center gap-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className={cn("h-3", width)} />
          </div>
        ))}
      </div>
    </div>
  )
}

function Settings() {
  return (
    <div className="mx-auto max-w-2xl space-y-10 px-10 py-8">
      {[0, 1, 2].map((panel) => (
        <div key={panel}>
          <Skeleton className="mb-3 h-4 w-32" />
          <div className="divide-y divide-border-subtle rounded-lg bg-card ring-1 ring-border">
            {[0, 1, 2].map((row) => (
              <div key={row} className="flex h-12 items-center gap-6 px-4">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="ml-auto h-7 w-40" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function Dashboard() {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-8 py-6">
      <div className="grid grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((tile) => (
          <Skeleton key={tile} className="h-28 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-72 rounded-lg" />
    </div>
  )
}

function MasterRows() {
  return titleWidths.slice(0, 5).map((width) => (
    <div key={width} className="flex h-12 items-center gap-3 px-4">
      <Skeleton className="size-2 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className={cn("h-3", width)} />
        <Skeleton className="h-2.5 w-16" />
      </div>
    </div>
  ))
}

function DialogRows() {
  return titleWidths.slice(0, 3).map((width) => (
    <div key={width} className="flex h-8 items-center gap-2 px-2">
      <Skeleton className="size-4" />
      <Skeleton className={cn("h-3", width)} />
    </div>
  ))
}

const kinds = {
  list: ListRows,
  board: Board,
  document: Document,
  settings: Settings,
  dashboard: Dashboard,
  "split-master": MasterRows,
  "dialog-list": DialogRows,
} as const

/**
 * The skeleton for one kind of page or part: `list` rows, a `board`'s columns, a `document` with
 * its rail, `settings` panels, a `dashboard`'s tiles and chart, the `split-master` list beside a
 * detail, or a `dialog-list` inside a dialog.
 */
export function PageSkeleton({ kind }: { kind: keyof typeof kinds }) {
  const shown = useShownAfter()
  const Shape = kinds[kind]
  return (
    <div role="status" aria-busy aria-label="Loading">
      {shown && (
        <div className="animate-in duration-150 fade-in">
          <Shape />
        </div>
      )}
    </div>
  )
}
